import { z } from "zod";

export enum ProfileProgramMembership {
  A1C_ONLY = "A1C_ONLY",
  MEDICARE = "MEDICARE",
  CDCDPP = "CDCDPP",
  ALLINONE_HEALTHCARE_CONCIERGE = "ALLINONE_HEALTHCARE_CONCIERGE",
  HEALTHY_WEIGHT_JOURNEY = "HEALTHY_WEIGHT_JOURNEY",
  DIABETES_AND_HEART_DISEASE_PREVENTION = "DIABETES_AND_HEART_DISEASE_PREVENTION"
}

export enum Condition {
  diabetesType1 = "Diabetes type 1",
  diabetesType2 = "Diabetes type 2",
  prediabetes = "Prediabetes",
  gestationalDiabetes = "Gestational diabetes",
  unknownDiabetes = "Diabetes - type unknown",
  highBloodPressure = "High blood pressure",
  highColesterol = "High cholesterol",
  kidneyOrRenalDisease = "Kidney or renal disease",
  kidneyDiseaseOrImpairment = "Chronic kidney disease or kidney impairment",
  kidneyDiseaseEndStageOrDialysis = "End-stage kidney or renal disease, or receiving dialysis",
  liverOrHepaticDisease = "Liver or hepatic disease",
  liverFailureOrCirrhosis = "Liver failure or cirrhosis",
  stroke = "Stroke",
  congestiveHeartFailure = "Congestive heart failure (CHF)",
  congestiveHeartFailureClassIIIOrIV = "Congestive heart failure (CHF) Class III or IV",
  coronaryArteryDisease = "Coronary artery disease (CAD)",
  peripheralArterialDisease = "Peripheral arterial disease (PAD)",
  acuteOrChronicMetabolicAcidosis = "Acute or chronic metabolic acidosis",
  obstructiveSleepApnea = "Obstructive Sleep Apnea",
  nonAlcoholicFattyLiverDisease = "Non-alcoholic fatty liver disease (NAFLD or NASH)",
  pcosPolycysticOvarianSyndrome = "Polycystic Ovarian Syndrome (PCOS)",
  gallbladderDisease = "Gallbladder disease (gallstones, inflamed or infected gallbladder)",
  medullaryThyroidCancer = "History of Medullary Thyroid Cancer or Multiple Endocrine Neoplasia syndrome",
  severeDiabeticGastroparesis = "Severe diabetic gastroparesis",
  pancreatitisOrTumors = "History of pancreatitis or tumors of the pancreas",
  obesity = "Obesity (BMI of 30 or greater)",
  organTransplant = "Organ transplant received (i.e. kidney, liver, heart, lung)",
  activeCancer = "Active cancer",
  heartAttackStrokeSurgeryInPast90Days = "Heart attack, stroke, or major surgery within the past 90 days",
  fattyLiverDiseaseNafldNashMasld = "Fatty liver disease (NAFLD, NASH, or MASLD)",
  currentlyOnGlp1 = "Currently on GLP1"
}

export enum Payer {
  Evernorth = "Evernorth"
}

export enum Employer {
  ThermoFisher = "Thermo Fisher",
  HarvardUniversity = "Harvard University",
  BankOfNewYork = "Bank of New York"
}

export const ProfileProgramMembershipSchema = z.nativeEnum(
  ProfileProgramMembership
);
export const ConditionSchema = z.nativeEnum(Condition);
export const EmployerSchema = z.nativeEnum(Employer);

export enum JourneyName {
  default = "Default"
}

export enum MedicalCategory {
  diabetes = "diabetes",
  hypertension = "hypertension",
  weightLoss = "weight-loss"
}

export enum DefaultCategory {
  all = "all",
  nutrition = "nutrition",
  fitness = "fitness",
  mentalHealth = "mental-health",
  heartHealth = "heart-health"
}

export type Category = DefaultCategory | MedicalCategory;

export type JourneyKeys = string;

export interface Caption {
  language?: string;
  srt?: {
    url?: string;
    path?: string;
    mimetype?: string;
  };
}

export interface EducationalContentResponseCategory {
  title: string;
  id: string;
  slug: string;
}

export interface EducationalContentResponseImage {
  metadata: {
    lqip: string;
    dimensions: {
      width: number;
      height: number;
      aspectRatio: number;
    };
  };
  path: string;
  url: string;
  size: number;
}

export interface EducationalContentResponseFeedItem {
  id: string;
  title: string;
  type: string;
  description: string;
  availableDate: string;
  categories: EducationalContentResponseCategory[];
  image: EducationalContentResponseImage | null;
  url: string | null;
  tags: string[];
  sanctuaryHealthVideo: null | {
    title: string;
    photoUrl?: string;
    approximateTime?: number;
    description?: string;
    landscapeVideo?: {
      path?: string;
      captions?: Caption[];
    };
  };
}

export interface EducationalContentResponseFeed {
  language: string;
  categories: EducationalContentResponseCategory[];
  feed: EducationalContentResponseFeedItem[];
  feedLength: number;
}

export type SanityJourneyTypes = "time-based";

export interface SanityJourneyContentOverview {
  _id: string;
  interval?: number;
  language?: string;
  listOfClasses?: {
    name?: string;
    language: string;
    listOfEducationalContent?: { _ref: string }[];
  }[];
  name?: string;
  type?: SanityJourneyTypes;
}

export interface SanityLessonContent {
  _id: string;
  title?: string;
  type?: string;
  description?: string;
  postUrl?: string;
  image?: {
    metadata: {
      lqip: string;
      dimensions: {
        width: number;
        height: number;
        aspectRatio: number;
      };
    };
    path: string;
    url: string;
    size: number;
  };
  video: null | {
    _id: string;
    title: string;
    photoUrl?: string;
    approximateTime?: number;
    description?: string;
    videoUrl: string;
    captions?: Caption[] | [];
  };
  categories?: {
    title: string;
    _id: string;
    slug: string;
  }[];
}

export interface SanityJourneyContentDetails {
  _id: string;
  interval?: number;
  language?: string;
  listOfClassesContentAvailable?: {
    name?: string;
    _id: string;
    _type: string;
    listOfEducationalContent?: SanityLessonContent[];
  }[];
  name?: string;
  type?: SanityJourneyTypes;
}
